'use client';
import { useEffect, useRef, useState } from 'react';
import { ETSY_URL } from '@/data/bundles';

import { BASE_IMG } from '@/data/bundles';

const heroImages = [
  { src: `${BASE_IMG}/14700_canva_page_templates_bundle.jpg`, label: '14,700+ Templates', style: { top: '5%', left: '5%', transform: 'rotate(-3deg)', zIndex: 3, width: '200px' }, animClass: 'animate-float-slow', delay: '0s' },
  { src: `${BASE_IMG}/100000_hd_stock_photos_bundle.jpg`, label: '100K+ Photos', style: { top: '30%', left: '55%', transform: 'rotate(4deg)', zIndex: 2, width: '180px' }, animClass: 'animate-float-medium', delay: '0.8s' },
  { src: `${BASE_IMG}/71_major_app_source_code_bundle.jpg`, label: '71 App Projects', style: { top: '55%', left: '8%', transform: 'rotate(2deg)', zIndex: 3, width: '160px' }, animClass: 'animate-float-fast', delay: '1.6s' },
  { src: `${BASE_IMG}/145_ui_kit_bundle_mega_pack.jpg`, label: '145 UI Kits', style: { top: '60%', left: '52%', transform: 'rotate(-2deg)', zIndex: 2, width: '170px' }, animClass: 'animate-float-slow', delay: '2.4s' },
  { src: `${BASE_IMG}/10000_fonts_svgs_icons_bundle_mega_pack.jpg`, label: '10K+ Fonts & SVGs', style: { top: '15%', left: '52%', transform: 'rotate(-1deg)', zIndex: 1, width: '155px' }, animClass: 'animate-float-medium', delay: '3.2s' },
];

export default function HeroSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollToContent = () => {
    const el = document.getElementById('whats-included');
    if (el) el?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden bg-hero-gradient noise-overlay"
      style={{ paddingTop: '8rem', paddingBottom: '4rem' }}
      aria-label="Hero section"
    >
      {/* Blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="blob absolute w-96 h-96 bg-primary" style={{ top: '10%', left: '-5%' }} />
        <div className="blob blob-2 absolute w-80 h-80 bg-accent" style={{ top: '40%', right: '-10%' }} />
        <div className="blob blob-3 absolute w-72 h-72 bg-secondary" style={{ bottom: '10%', left: '30%' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="flex flex-col gap-6 lg:gap-8">
            {/* Badge */}
            <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0ms' }}>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-sm font-semibold border" style={{ color: 'var(--secondary-foreground)', borderColor: 'rgba(124,58,237,0.2)' }}>
                <span className="animate-badge-pulse">✦</span>
                All 43 Bundles, One Purchase
              </span>
            </div>

            {/* Heading */}
            <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '100ms' }}>
              <h1 className="hero-heading" style={{ color: 'var(--foreground)' }}>
                Everything You Need to{' '}
                <span className="text-gradient">Create, Publish &amp; Sell</span>{' '}
                Digital Products — In One Massive Bundle.
              </h1>
            </div>

            {/* Description */}
            <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '200ms' }}>
              <p className="text-lg leading-relaxed max-w-xl" style={{ color: 'var(--muted-foreground)' }}>
                Get 43 premium collections covering Canva templates, KDP resources, graphics, fonts, stock photos, UI kits, source code, eBooks, planners, business tools, wall art and much more — without buying each collection separately.
              </p>
            </div>

            {/* Price */}
            <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '300ms' }}>
              <div className="flex items-baseline gap-3">
                <span className="text-sm font-medium" style={{ color: 'var(--muted-foreground)' }}>Complete 43-Bundle Collection</span>
              </div>
              <div className="flex items-baseline gap-3 mt-1">
                <span className="text-5xl font-extrabold tracking-tight" style={{ color: 'var(--foreground)' }}>$79</span>
                <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>One-time purchase • Digital delivery</span>
              </div>
            </div>

            {/* CTAs */}
            <div className={`flex flex-col sm:flex-row gap-3 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '400ms' }}>
              <a href={ETSY_URL} target="_blank" rel="noopener noreferrer" className="btn-primary text-base py-4 px-6 justify-center sm:justify-start">
                🚀 Get All 43 Bundles — $79
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width={18} height={18}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </a>
              <button onClick={scrollToContent} className="btn-secondary text-base py-4 px-6 justify-center">
                Explore What&apos;s Inside ↓
              </button>
            </div>

            {/* Trust badges */}
            <div className={`flex flex-wrap gap-x-4 gap-y-2 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '500ms' }}>
              {['✓ One-Time Purchase', '✓ 43 Collections', '✓ Digital Delivery', '✓ Multiple Checkout Options']?.map((t) => (
                <span key={t} className="text-sm font-medium" style={{ color: 'var(--muted-foreground)' }}>{t}</span>
              ))}
            </div>
          </div>

          {/* Right - Floating Images */}
          <div className="relative h-80 sm:h-96 lg:h-[520px]" aria-hidden="true">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-64 h-64 rounded-full blur-3xl" style={{ background: 'rgba(124,58,237,0.1)' }} />
            </div>
            {heroImages?.map((img, i) => (
              <div
                key={i}
                className={`absolute glass rounded-xl overflow-hidden shadow-2xl ${img?.animClass}`}
                style={{ ...img?.style, animationDelay: img?.delay }}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={img?.src}
                    alt={img?.label}
                    width={200}
                    height={112}
                    className="w-full h-full object-cover"
                    loading={i === 0 ? 'eager' : 'lazy'}
                  />
                </div>
                <div className="px-2.5 py-1.5" style={{ background: 'rgba(15,22,41,0.9)' }}>
                  <span className="text-xs font-semibold" style={{ color: 'var(--secondary-foreground)' }}>{img?.label}</span>
                </div>
              </div>
            ))}
            {/* 43 badge */}
            <div
              className="absolute glass rounded-2xl px-4 py-3 text-center animate-pulse-glow"
              style={{ top: '38%', left: '28%', zIndex: 5, transform: 'translate(-50%, -50%)' }}
            >
              <div className="text-2xl font-extrabold text-gradient">43</div>
              <div className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>Collections</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Down */}
      <button
        onClick={scrollToContent}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 transition-colors animate-bounce"
        style={{ color: 'var(--muted-foreground)' }}
        aria-label="Scroll down to see what's inside"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width={28} height={28}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
    </section>
  );
}
